import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import { useFeature } from '../../ui/FeaturesProvider';
import {
  fetchModelHistory,
  fetchModelStatus,
  triggerModelTraining,
} from '../../services/contactOptimizationService';
import type { ContactOptimizationModelRun, ModelCalibrationBin } from '../../types/contactOptimization';
import { formatDateRange } from '../../utils/date';
import SettingsPageHeader from '../settings/components/SettingsPageHeader';
import ExplainerNote, { ExplainerToggle } from './ExplainerNote';
import ScopeBanner from './ScopeBanner';
import { buildContactOptGridSx, CONTACT_OPT_PAGE_SIZE_OPTIONS, makeContactOptGridToolbar } from './contactOptGridKit';
import { CalibrationChart, FeatureImportanceBarChart } from './modelCharts';

const POLL_MS = 6000;
const CalibrationGridToolbar = makeContactOptGridToolbar('model-calibration');
const HistoryGridToolbar = makeContactOptGridToolbar('model-history');

const CALIBRATION_COLUMNS: GridColDef<ModelCalibrationBin & { id: number }>[] = [
  {
    field: 'bin_low',
    headerName: 'Score range',
    minWidth: 130,
    flex: 0.7,
    sortable: false,
    renderCell: (params) => `${pct(params.row.bin_low, 2)}\u2013${pct(params.row.bin_high, 2)}`,
  },
  {
    field: 'predicted_mean',
    headerName: 'Predicted',
    type: 'number',
    minWidth: 100,
    flex: 0.5,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (value: number | null) => pct(value, 2),
  },
  {
    field: 'actual_rate',
    headerName: 'Actual',
    type: 'number',
    minWidth: 100,
    flex: 0.5,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (value: number | null) => pct(value, 2),
  },
  {
    field: 'count',
    headerName: 'Leads',
    type: 'number',
    minWidth: 90,
    flex: 0.4,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (value: number | null) => (value == null ? '\u2013' : value.toLocaleString()),
  },
];

const HISTORY_COLUMNS: GridColDef<ContactOptimizationModelRun>[] = [
  {
    field: 'version',
    headerName: 'Version',
    type: 'number',
    minWidth: 90,
    flex: 0.4,
    valueFormatter: (value: number | null) => (value == null ? '\u2013' : `v${value}`),
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 110,
    flex: 0.5,
    renderCell: (params) => <StatusChip status={params.value as ContactOptimizationModelRun['status']} />,
  },
  {
    field: 'trained_at',
    headerName: 'Trained',
    minWidth: 170,
    flex: 0.8,
    valueFormatter: (value: string | null) => fmtDateTime(value),
  },
  {
    field: 'row_count',
    headerName: 'Calls used',
    type: 'number',
    minWidth: 100,
    flex: 0.5,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (value: number | null) => (value == null ? '\u2013' : value.toLocaleString()),
  },
  {
    field: 'auc',
    headerName: 'Ranking accuracy',
    description: 'How well this model ranks leads by pickup likelihood. 0.5 = no better than a coin flip, 1.0 = perfect.',
    type: 'number',
    minWidth: 130,
    flex: 0.4,
    align: 'right',
    headerAlign: 'right',
    valueGetter: (_value, row) => row.metrics?.auc_lightgbm ?? null,
    valueFormatter: (value: number | null) => (value == null ? '\u2013' : value.toFixed(3)),
  },
  {
    field: 'lift_at_capacity',
    headerName: 'Best-leads advantage',
    description: "How much better the connect rate would be if we could only call this model's top 20% of leads, vs. calling everyone at random.",
    type: 'number',
    minWidth: 150,
    flex: 0.6,
    align: 'right',
    headerAlign: 'right',
    valueGetter: (_value, row) => row.metrics?.lift_at_capacity?.lift_vs_random ?? null,
    valueFormatter: (value: number | null) => (value == null ? '\u2013' : `${value.toFixed(1)}x`),
  },
];

function pct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '\u2013';
  return `${(value * 100).toFixed(digits)}%`;
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '\u2013';
  const d = new Date(value.endsWith('Z') ? value : `${value}Z`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function StatusChip({ status }: { status: ContactOptimizationModelRun['status'] }) {
  const map: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
    active: { label: 'Active', color: 'success' },
    training: { label: 'Training\u2026', color: 'warning' },
    failed: { label: 'Failed', color: 'error' },
    archived: { label: 'Archived', color: 'default' },
  };
  const entry = map[status] ?? { label: status, color: 'default' as const };
  return <Chip label={entry.label} color={entry.color} size="small" variant={entry.color === 'default' ? 'outlined' : 'filled'} />;
}

function MetricBlock({ label, value, tooltip }: { label: string; value: string; tooltip?: string }) {
  return (
    <Box sx={{ minWidth: 140 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          {label}
        </Typography>
        {tooltip ? (
          <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlined sx={{ fontSize: 12, color: 'text.disabled' }} />
          </Tooltip>
        ) : null}
      </Stack>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  );
}

function ActiveModelCard({ model }: { model: ContactOptimizationModelRun }) {
  const theme = useTheme();
  const gridSx = React.useMemo(() => buildContactOptGridSx(theme), [theme]);
  const [showMetricsHelp, setShowMetricsHelp] = React.useState(false);
  const metrics = model.metrics;
  const lift = metrics?.lift_at_capacity;
  const pva = metrics?.predicted_vs_actual_totals;
  const leakage = metrics?.leakage_test;
  // Postgres JSONB doesn't preserve key insertion order, so sort explicitly
  // rather than trusting the backend's dict order to survive the round trip.
  const topFeatures = metrics
    ? Object.entries(metrics.feature_importance).sort((a, b) => b[1] - a[1]).slice(0, 8)
    : [];

  return (
    <Paper elevation={0} sx={(t) => ({ p: 2.5, borderRadius: `${t.ds.radius.md}px`, border: `1px solid ${t.tokens.border.subtle}` })}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Version {model.version}
            </Typography>
            <StatusChip status={model.status} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Trained {fmtDateTime(model.trained_at)}
          </Typography>
        </Box>
        <ExplainerToggle title="What these numbers mean" open={showMetricsHelp} onClick={() => setShowMetricsHelp((v) => !v)} />
      </Stack>

      {/* Every metric below is measured on the validation window, never on
          the training window -- stated as an explicit "showing vs.
          measured-against" pair rather than one run-on sentence. */}
      <ScopeBanner
        showing={{ label: 'Training window', detail: `${formatDateRange(model.training_window_start, model.training_window_end)} \u00b7 ${model.row_count?.toLocaleString() ?? '\u2013'} calls` }}
        comparedAgainst={{ label: 'Validation window (metrics measured here)', detail: `${formatDateRange(model.validation_window_start, model.validation_window_end)} \u00b7 unseen by training` }}
      />

      <ExplainerNote title="What these numbers mean" open={showMetricsHelp}>
        {`The model is trained on **${model.row_count?.toLocaleString() ?? 'the'} historical calls** (the training period above) and tested on a separate, later batch of calls it never saw during training (the test period). Everything in this row and below is measured on that unseen batch, not on the training data, so it reflects real predictive skill rather than the model just memorizing history.

- **Base connect rate** — simply how often a call connected in that training data, for context.
- **Ranking accuracy** (0.5 = no better than a coin flip, 1.0 = perfect) — measures how well the model ranks leads by pickup likelihood. Two versions are shown side by side: our main model and a much simpler backup approach used as a sanity check. If the main model isn't meaningfully better than the simple one, that's a red flag that the extra complexity isn't earning its keep.
- **Best-leads advantage** — if we could only call the top 20% of leads by score, how much better would that group's connect rate be than a random 20%?
- **Prediction accuracy** — checks the model isn't systematically over- or under-confident: 100% means its predicted number of connects matched what actually happened on the unseen test calls.`}
      </ExplainerNote>

      <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <MetricBlock label="Calls used to train" value={model.row_count?.toLocaleString() ?? '\u2013'} />
        <MetricBlock label="Base connect rate" value={pct(model.base_rate, 2)} />
        <MetricBlock label="Ranking accuracy (main model)" value={metrics ? metrics.auc_lightgbm.toFixed(3) : '\u2013'} tooltip="Measured on real calls the model had never seen before." />
        <MetricBlock label="Ranking accuracy (simple baseline)" value={metrics ? metrics.auc_logistic_challenger.toFixed(3) : '\u2013'} tooltip="A much simpler backup approach, used only as a sanity check that the main model's extra complexity is worth it." />
        <MetricBlock
          label="Best-leads advantage"
          value={lift?.lift_vs_random != null ? `${lift.lift_vs_random.toFixed(1)}x` : '\u2013'}
          tooltip={lift ? `The top ${Math.round(lift.fraction * 100)}% of leads by score connected at ${pct(lift.top_connect_rate, 1)}, vs ${pct(lift.overall_connect_rate, 1)} if we'd called everyone with no ranking, measured on calls the model had never seen.` : undefined}
        />
        <MetricBlock
          label="Prediction accuracy"
          value={pva?.ratio != null ? pct(pva.ratio, 0) : '\u2013'}
          tooltip={pva ? `Predicted ${Math.round(pva.predicted_connects).toLocaleString()} connects, actually got ${Math.round(pva.actual_connects).toLocaleString()}, on calls the model had never seen before.` : undefined}
        />
      </Stack>

      {leakage ? (
        <Alert severity={leakage.passed ? 'success' : 'error'} sx={{ mb: 2.5, borderRadius: '8px' }}>
          Cheating check {leakage.passed ? 'passed' : 'failed'} &mdash; nothing the model uses looked suspiciously
          too good to be true{leakage.flagged_features.length ? `, though ${leakage.flagged_features.join(', ')} is worth a second look` : ''}.
          {' '}<Typography component="span" variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>
            This checks that the model isn't secretly "cheating" by using information that only exists after
            a call already connected (for example, a field that only gets filled in once someone picks up
            would make the model look artificially great in testing, then fail in the real world where that
            information isn't available yet). "Passed" means nothing like that was found.
          </Typography>
        </Alert>
      ) : null}

      {topFeatures.length ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
            What matters most for predicting pickup
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.5 }}>
            What the model leans on most to predict a pickup, ranked by how much each factor mattered
            during training. Longer bar = more influential, relative to the top factor.
          </Typography>
          <FeatureImportanceBarChart topFeatures={topFeatures} />
        </Box>
      ) : null}

      {metrics?.calibration_bins?.length ? (
        <Box sx={{ mt: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Can we trust the percentages themselves?
            </Typography>
            <Tooltip title="Predicted average vs. actual connect rate within each score group. Close together means the percentages themselves are trustworthy, not just the ranking." arrow>
              <InfoOutlined sx={{ fontSize: 12, color: 'text.disabled' }} />
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.5 }}>
            Leads are split into 10 equal-sized groups by the score the model gave them, from lowest
            predicted pickup chance to highest. For each group, "Predicted" is the model's own average
            guess and "Actual" is what really happened, on calls the model had never seen before. Close
            numbers in each row mean the model's percentages can be taken at face value (e.g. a "40%
            chance" really does mean roughly 4 in 10), not just that it puts leads in the right order.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <CalibrationChart bins={metrics.calibration_bins} />
          </Box>
          <Paper elevation={0} sx={(t) => ({ border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.sm}px`, overflow: 'hidden' })}>
            <DataGrid
              rows={metrics.calibration_bins.map((bin, idx) => ({ ...bin, id: idx }))}
              columns={CALIBRATION_COLUMNS}
              autoHeight
              disableRowSelectionOnClick
              hideFooter
              sortingMode="client"
              slots={{ toolbar: CalibrationGridToolbar }}
              localeText={{ toolbarQuickFilterPlaceholder: 'Search score range\u2026' }}
              sx={gridSx}
            />
          </Paper>
        </Box>
      ) : null}
    </Paper>
  );
}

export default function ModelPage() {
  const theme = useTheme();
  const historyGridSx = React.useMemo(() => buildContactOptGridSx(theme), [theme]);
  const feature = useFeature('contact_optimization');
  const [active, setActive] = React.useState<ContactOptimizationModelRun | null>(null);
  const [training, setTraining] = React.useState<ContactOptimizationModelRun | null>(null);
  const [lastFailed, setLastFailed] = React.useState<ContactOptimizationModelRun | null>(null);
  const [history, setHistory] = React.useState<ContactOptimizationModelRun[]>([]);
  const [hasAnyVersion, setHasAnyVersion] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [startingTrain, setStartingTrain] = React.useState(false);

  const loadStatus = React.useCallback(async () => {
    const [status, hist] = await Promise.all([fetchModelStatus(), fetchModelHistory()]);
    setActive(status.active);
    setTraining(status.training);
    setLastFailed(status.last_failed);
    setHasAnyVersion(status.has_any_version);
    setHistory(hist.versions);
  }, []);

  React.useEffect(() => {
    if (!feature.enabled) return;
    setLoading(true);
    setError(null);
    loadStatus()
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load model status'))
      .finally(() => setLoading(false));
  }, [feature.enabled, loadStatus]);

  React.useEffect(() => {
    if (!training) return;
    const id = window.setInterval(() => {
      loadStatus().catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [training, loadStatus]);

  const handleRetrain = async () => {
    setStartingTrain(true);
    setError(null);
    try {
      const result = await triggerModelTraining();
      await loadStatus();
      if (result) setTraining(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start training');
    } finally {
      setStartingTrain(false);
    }
  };

  if (!feature.enabled) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <Alert severity="info">
          Contact Optimization is not turned on for this workspace. Enable it under Settings &rarr; Features.
        </Alert>
      </Box>
    );
  }

  if (loading && !hasAnyVersion) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <SettingsPageHeader description="The prediction model behind Contact Pulse's model tiles, Timing's 'Compare states' view, and Queue's ranking." />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {training
            ? 'Training in progress — this page refreshes automatically.'
            : hasAnyVersion
              ? 'Retraining pulls the full call history fresh and replaces the active model when it succeeds.'
              : 'No model has been trained yet.'}
        </Typography>
        <Button
          variant="contained"
          startIcon={training ? <CircularProgress size={16} color="inherit" /> : <RefreshRounded />}
          onClick={handleRetrain}
          disabled={!!training || startingTrain}
        >
          {training ? 'Training\u2026' : 'Retrain'}
        </Button>
      </Stack>

      {training ? (
        <Paper elevation={0} sx={(t) => ({ p: 2.5, mb: 2.5, borderRadius: `${t.ds.radius.md}px`, border: `1px solid ${t.tokens.border.subtle}` })}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Version {training.version} training since {fmtDateTime(training.started_at)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Pulling the full call history, building the main model and a simple backup model, checking the
            percentages are trustworthy, and running the cheating check. Usually takes a few minutes.
          </Typography>
          <LinearProgress />
        </Paper>
      ) : null}

      {!training && lastFailed && (!active || lastFailed.version > active.version) ? (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: '8px' }}>
          Version {lastFailed.version} failed: {lastFailed.error_message ?? 'unknown error'}. The previous active
          model (if any) is unaffected.
        </Alert>
      ) : null}

      {active ? (
        <Box sx={{ mb: 3 }}>
          <ActiveModelCard model={active} />
        </Box>
      ) : !training ? (
        <Alert severity="info" sx={{ mb: 3, borderRadius: '8px' }}>
          No active model yet. Click Retrain to train the first version on the full call history.
        </Alert>
      ) : null}

      {history.length > 1 ? (
        <Paper elevation={0} sx={(t) => ({ p: 2, borderRadius: `${t.ds.radius.md}px`, border: `1px solid ${t.tokens.border.subtle}` })}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            History
          </Typography>
          <Paper elevation={0} sx={(t) => ({ border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.sm}px`, overflow: 'hidden' })}>
            <DataGrid
              rows={history}
              columns={HISTORY_COLUMNS}
              getRowId={(row) => row.model_id}
              autoHeight
              disableRowSelectionOnClick
              sortingMode="client"
              paginationMode="client"
              pageSizeOptions={CONTACT_OPT_PAGE_SIZE_OPTIONS}
              initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
              slots={{ toolbar: HistoryGridToolbar }}
              localeText={{ toolbarQuickFilterPlaceholder: 'Search version, status\u2026' }}
              sx={historyGridSx}
            />
          </Paper>
        </Paper>
      ) : null}
    </Box>
  );
}
